import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Calendar as CalIcon,
  Clock,
  PawPrint,
  Sparkles,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { toast, Toaster } from "sonner";
import { api, apiError } from "../lib/api";
import { LOGO_URL, CONTACT } from "../lib/data";

const SIZE_LABELS = {
  small: "Small (under 10 kg)",
  medium: "Medium (10 – 25 kg)",
  large: "Large (25+ kg)",
};

const STEPS = ["Service", "Size", "Date & Time", "Your Details", "Confirm"];

function fmtNZ(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-NZ", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Pacific/Auckland",
  });
}

function fmtTimeOnly(iso) {
  return new Date(iso).toLocaleString("en-NZ", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Pacific/Auckland",
  });
}

export default function Book() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [size, setSize] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [details, setDetails] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    dog_name: "",
    dog_breed: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch(() => toast.error("Couldn't load services."));
  }, []);

  const hasSizes = service && !("default" in (service.prices || {}));
  const stepList = useMemo(() => (hasSizes ? STEPS : STEPS.filter((_, i) => i !== 1)), [hasSizes]);

  // Fetch slots when date changes
  useEffect(() => {
    if (!service || !date) return;
    const dateStr = date.toISOString().slice(0, 10);
    setSlotsLoading(true);
    setSlot(null);
    api
      .get(`/availability`, { params: { service_id: service.id, date: dateStr } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => toast.error("Couldn't load availability."))
      .finally(() => setSlotsLoading(false));
  }, [service, date]);

  const goNext = () => {
    if (step === 0 && !service) return toast.error("Choose a service to continue.");
    if (step === 1 && hasSizes && !size) return toast.error("Choose a size.");
    if (step === 2 && !slot) return toast.error("Choose a date and time.");
    if (step === 3) {
      const missing = ["customer_name", "customer_email", "customer_phone", "dog_name"].filter(
        (k) => !details[k].trim()
      );
      if (missing.length) return toast.error("Please fill in your details.");
    }
    // Skip size step if not needed
    if (step === 0 && !hasSizes) {
      setSize(null);
      return setStep(2);
    }
    setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step === 2 && !hasSizes) return setStep(0);
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        service_id: service.id,
        size,
        start: slot,
        ...details,
      });
      setConfirmation(r.data);
      setStep(4);
    } catch (e) {
      toast.error(apiError(e, "Sorry, that didn't go through. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const price = service && (service.prices.default ?? (size ? service.prices[size] : null));

  return (
    <div className="min-h-screen bg-cream relative">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#F9F6F0",
            color: "#4A3525",
            border: "1px solid #E6DEC3",
            borderRadius: "16px",
          },
        }}
      />

      {/* Top bar */}
      <header className="border-b border-[#E6DEC3]/60 bg-cream/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="book-logo">
            <img src={LOGO_URL} alt="Barks & Co" className="h-11 w-11 rounded-full" />
            <div>
              <div className="font-serif text-lg text-brown-dark leading-tight">
                Barks &amp; Co
              </div>
              <div className="eyebrow" style={{ fontSize: "0.6rem" }}>
                Book An Appointment
              </div>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="book-back-home"
            className="text-sm text-brown-warm hover:text-lilac-dark transition-colors"
          >
            ← Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16 relative">
        {step !== 4 && (
          <div className="mb-10">
            <Stepper steps={stepList} current={Math.min(step, stepList.length - 1)} />
          </div>
        )}

        {/* Step content */}
        {step === 0 && (
          <StepCard title="Choose a service" sub="Pick the spa day your pup is in for.">
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <button
                  key={s.id}
                  data-testid={`service-option-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => {
                    setService(s);
                    setSize(null);
                  }}
                  className={`text-left p-6 rounded-[1.5rem] border transition-all ${
                    service?.id === s.id
                      ? "border-lilac-dark bg-lilac-soft"
                      : "border-[#E6DEC3] bg-white/50 hover:border-lilac"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-2xl text-brown-dark">{s.name}</h3>
                    {service?.id === s.id && <Check size={18} className="text-lilac-dark mt-2 shrink-0" />}
                  </div>
                  <p className="text-sm text-text-secondary mt-2 leading-relaxed">{s.description}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-brown-warm">
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} /> {s.duration_minutes} min
                    </span>
                    <span className="font-medium text-brown-dark">
                      {s.prices.default != null
                        ? `NZ$${s.prices.default}`
                        : `from NZ$${Math.min(...Object.values(s.prices))}`}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </StepCard>
        )}

        {step === 1 && hasSizes && (
          <StepCard title="Your pup's size" sub="So we can prepare the right service and quote.">
            <div className="grid sm:grid-cols-3 gap-4">
              {Object.entries(service.prices).map(([k, v]) => (
                <button
                  key={k}
                  data-testid={`size-option-${k}`}
                  onClick={() => setSize(k)}
                  className={`p-6 rounded-[1.5rem] border text-left transition-all ${
                    size === k
                      ? "border-lilac-dark bg-lilac-soft"
                      : "border-[#E6DEC3] bg-white/50 hover:border-lilac"
                  }`}
                >
                  <div className="eyebrow mb-2">{k}</div>
                  <div className="font-serif text-xl text-brown-dark mb-2">
                    {SIZE_LABELS[k] || k}
                  </div>
                  <div className="text-brown-warm font-medium">NZ${v}</div>
                </button>
              ))}
            </div>
          </StepCard>
        )}

        {step === 2 && (
          <StepCard title="Choose a date & time" sub="Available slots are based on the studio's working hours.">
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="bg-white/60 border border-[#E6DEC3] rounded-[1.5rem] p-4">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  data-testid="booking-calendar"
                  className="!p-0"
                />
              </div>
              <div>
                <div className="eyebrow mb-3 flex items-center gap-2">
                  <CalIcon size={14} />
                  {date ? date.toDateString() : "Pick a date"}
                </div>
                {!date && (
                  <p className="text-text-secondary text-sm">
                    Select a date on the calendar to see times.
                  </p>
                )}
                {date && slotsLoading && (
                  <div className="text-brown-warm flex items-center gap-2 text-sm">
                    <Loader2 className="animate-spin" size={14} />
                    Loading available times…
                  </div>
                )}
                {date && !slotsLoading && slots.length === 0 && (
                  <div
                    className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-4 text-sm text-brown-warm"
                    data-testid="no-slots-message"
                  >
                    No available times on that date. Please try another day.
                  </div>
                )}
                {date && !slotsLoading && slots.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="slots-grid">
                    {slots.map((s) => (
                      <button
                        key={s}
                        data-testid={`slot-${s}`}
                        onClick={() => setSlot(s)}
                        className={`px-3 py-2.5 rounded-full text-sm border transition-all ${
                          slot === s
                            ? "bg-brown-dark text-cream border-brown-dark"
                            : "bg-white border-[#E6DEC3] text-brown-dark hover:border-lilac"
                        }`}
                      >
                        {fmtTimeOnly(s)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </StepCard>
        )}

        {step === 3 && (
          <StepCard title="Your details" sub="So we can send you the confirmation and the studio address.">
            <div className="grid sm:grid-cols-2 gap-5">
              <Input label="Your name *" value={details.customer_name}
                onChange={(v) => setDetails((d) => ({ ...d, customer_name: v }))}
                testId="book-customer-name" />
              <Input label="Phone *" value={details.customer_phone}
                onChange={(v) => setDetails((d) => ({ ...d, customer_phone: v }))}
                testId="book-customer-phone" />
              <Input label="Email *" type="email" value={details.customer_email}
                onChange={(v) => setDetails((d) => ({ ...d, customer_email: v }))}
                testId="book-customer-email" />
              <Input label="Dog's name *" value={details.dog_name}
                onChange={(v) => setDetails((d) => ({ ...d, dog_name: v }))}
                testId="book-dog-name" />
              <Input label="Breed" value={details.dog_breed}
                onChange={(v) => setDetails((d) => ({ ...d, dog_breed: v }))}
                testId="book-dog-breed" />
            </div>
            <div className="mt-5">
              <label className="block eyebrow mb-2">Anything we should know? (optional)</label>
              <textarea
                value={details.notes}
                onChange={(e) => setDetails((d) => ({ ...d, notes: e.target.value }))}
                data-testid="book-notes"
                rows={3}
                placeholder="Sensitive areas, behaviour notes, preferred style..."
                className="w-full bg-white border border-[#E6DEC3] rounded-2xl px-5 py-3.5 text-brown-dark focus:ring-1 focus:ring-lilac focus:border-lilac focus:outline-none resize-none"
              />
            </div>

            {/* Summary */}
            <div className="mt-7 bg-lilac-soft/60 border border-[#E6DEC3] rounded-2xl p-5 text-sm">
              <div className="eyebrow mb-3">Booking summary</div>
              <SummaryLine label="Service" value={service?.name} />
              {size && <SummaryLine label="Size" value={SIZE_LABELS[size] || size} />}
              <SummaryLine label="When" value={slot ? fmtNZ(slot) : ""} />
              <SummaryLine label="Duration" value={`${service?.duration_minutes} min`} />
              <SummaryLine label="Price" value={`NZ$${price}`} bold />
            </div>
          </StepCard>
        )}

        {step === 4 && confirmation && (
          <div
            className="text-center bg-cream-alt border border-[#E6DEC3] rounded-[2rem] p-10 md:p-14"
            data-testid="booking-success"
          >
            <div className="h-16 w-16 rounded-full bg-lilac-soft flex items-center justify-center mx-auto mb-6">
              <PawPrint size={28} className="text-lilac-dark" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-brown-dark">
              You're booked in.
            </h2>
            <p className="mt-4 text-text-secondary max-w-md mx-auto">
              We've sent a confirmation with all the details (including the
              studio address and a calendar invite) to{" "}
              <strong className="text-brown-dark">{confirmation.customer_email}</strong>.
            </p>
            <div className="mt-8 mx-auto max-w-md bg-white rounded-2xl border border-[#E6DEC3] p-6 text-left text-sm">
              <SummaryLine label="Service" value={confirmation.service_name} />
              {confirmation.size && <SummaryLine label="Size" value={SIZE_LABELS[confirmation.size] || confirmation.size} />}
              <SummaryLine label="When" value={fmtNZ(confirmation.start)} />
              <SummaryLine label="Dog" value={confirmation.dog_name} />
              <SummaryLine label="Price" value={`NZ$${confirmation.price}`} bold />
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-outline" data-testid="book-success-home">
                Back to site
              </Link>
              <button
                onClick={() => {
                  setService(null); setSize(null); setDate(null); setSlot(null);
                  setDetails({ customer_name: "", customer_email: "", customer_phone: "", dog_name: "", dog_breed: "", notes: "" });
                  setConfirmation(null); setStep(0);
                }}
                className="btn-primary"
                data-testid="book-success-another"
              >
                Book Another
              </button>
            </div>
            <p className="text-xs text-brown-warm mt-6">
              Need to change anything? Email {CONTACT.email}.
            </p>
          </div>
        )}

        {/* Nav buttons */}
        {step !== 4 && (
          <div className="mt-10 flex items-center justify-between" data-testid="book-nav-buttons">
            <button
              onClick={goBack}
              disabled={step === 0}
              data-testid="book-back-button"
              className="inline-flex items-center gap-2 text-brown-warm hover:text-lilac-dark disabled:opacity-30 disabled:hover:text-brown-warm transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 3 && (
              <button onClick={goNext} className="btn-primary" data-testid="book-next-button">
                Continue <ArrowRight size={16} />
              </button>
            )}
            {step === 3 && (
              <button
                onClick={submit}
                disabled={submitting}
                className="btn-primary disabled:opacity-60"
                data-testid="book-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Confirming…
                  </>
                ) : (
                  <>
                    Confirm Booking <Sparkles size={16} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Stepper({ steps, current }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto" data-testid="book-stepper">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div
            className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
              i < current
                ? "bg-lilac-dark text-cream"
                : i === current
                ? "bg-brown-dark text-cream"
                : "bg-cream-alt text-brown-warm border border-[#E6DEC3]"
            }`}
          >
            {i < current ? <Check size={14} /> : i + 1}
          </div>
          <span
            className={`text-xs sm:text-sm font-medium ${
              i === current ? "text-brown-dark" : "text-brown-warm/70"
            }`}
          >
            {label}
          </span>
          {i < steps.length - 1 && <div className="h-px w-6 sm:w-10 bg-[#E6DEC3]" />}
        </div>
      ))}
    </div>
  );
}

function StepCard({ title, sub, children }) {
  return (
    <div className="bg-cream-alt border border-[#E6DEC3] rounded-[2rem] p-7 md:p-10">
      <h2 className="font-serif text-3xl md:text-4xl text-brown-dark">{title}</h2>
      {sub && <p className="mt-2 text-text-secondary">{sub}</p>}
      <div className="mt-7">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block eyebrow mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-white border border-[#E6DEC3] rounded-full px-5 py-3.5 text-brown-dark placeholder:text-brown-warm/50 focus:ring-1 focus:ring-lilac focus:border-lilac focus:outline-none"
      />
    </div>
  );
}

function SummaryLine({ label, value, bold }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-[#E6DEC3]/60 last:border-0">
      <span className="eyebrow !text-brown-warm">{label}</span>
      <span className={`text-brown-dark ${bold ? "font-medium text-base" : ""}`}>
        {value}
      </span>
    </div>
  );
}
